import { Box } from '@mui/material'
import { ReactNode, useEffect, useRef, useState } from 'react'

import TFloatingPopover from 'components/popovers/TFloatingPopover'
import { usePayment } from 'components/providers/PaymentProvider'
import { Placement } from '@floating-ui/react'
import { PopupSurfaceLevel } from 'components/popovers/TFloatingPopover'

export interface PopupInstance {
  hide: () => void
}

interface PopupProps {
  children: ReactNode
  content: ReactNode
  className?: string
  placement?: 'bottom' | 'right' | 'top' | 'left'
  appendTo?: 'parent'
  open?: boolean
  onMount?: (instance: PopupInstance) => void
  onKeyboardEnter?: () => void
  onKeyboardEsc?: () => void
  setOpen?: (b: boolean) => void
  onClickAwayAction?: () => void
  width?: string
  onHide?: () => void
  closeOnTouchOutside?: boolean
  fallbackPlacements?: Placement[]
  rightClickClose?: boolean
  padding?: number
  surfaceLevel?: PopupSurfaceLevel
}

export default function Popup(props: PopupProps) {
  const payments = usePayment()

  useEffect(() => {
    const keyEnter = async (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (props.onKeyboardEnter) props.onKeyboardEnter()
      } else if (event.key === 'Escape') {
        if (props.onKeyboardEsc) props.onKeyboardEsc()
      }
    }

    document.addEventListener('keydown', keyEnter)

    return () => {
      document.removeEventListener('keydown', keyEnter)
    }
  }, [props.open])

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const popupRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!props.open) {
      setAnchorEl(null)
    } else {
      if (popupRef.current) {
        setAnchorEl(popupRef.current)
      }
    }
  }, [props.open])

  return (
    <Box
      ref={popupRef}
      onClick={(e) => {
        if (anchorEl === e.currentTarget) {
          setAnchorEl(null)
          return
        }
        setAnchorEl(e.currentTarget)
      }}
    >
      {props.children}
      <TFloatingPopover
        padding={props.padding}
        surfaceLevel={props.surfaceLevel ?? 'primary'}
        open={(props.open ?? false) && anchorEl !== null}
        onClose={() => {
          setAnchorEl(null)
          props.onHide?.()
        }}
        defaultPlacement={props.placement}
        fallbackPlacements={props.fallbackPlacements}
        anchorElement={anchorEl}
        content={props.content}
        onOutsideClick={props.onClickAwayAction}
        closeOnTouchOutside={
          !payments.isOpen && (props.closeOnTouchOutside ?? true)
        } // Pass preventClose to TFloatingPopover
        rightClickClose={props.rightClickClose}
      />
    </Box>
  )
}

interface ContentContainerProps {
  setOpen?: (b: boolean) => void
  open: boolean
  content: ReactNode
  onClickAwayAction?: () => void
}
